import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'react-intl';
import { Section, SectionCard, Row, RowActionEnum, DividerGroup } from './DocumentLayout';
import { useDispatch } from 'react-redux';
import { updateChangeRequestStatus } from 'src/app/modules/Recruit/network/personalParticularCrud';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import {
  ChangeRequestItem,
  PdfFileTypeEnum,
  ChangeRequestSectionEnum,
} from '../../../../types/personal-particular-types';
import { useDocument } from './useDocument';
import { formatAmount, formatDate } from './document.utils';
import { ChangeRequestsHistoryDialog, ChangeRequestsHistoryrRef } from './ChangeRequestsHistoryDialog';
import { useLang } from 'src/app/i18n';

const useStyles = makeStyles()(() => ({
  fixedWidthCell: {
    width: 300,
  },
  itemContent: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 32,
    fontSize: '1rem',
  },
  submittedDate: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 20,
    fontSize: '1rem',
    color: '#848484',
  },
  compactItemContent: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 23,
    fontSize: '1rem',
  },
  historyLink: {
    marginLeft: 6,
    color: '#FF0000',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
}));

const getLoadingKey = (action: RowActionEnum, section: string) => `${action}-${section}`;
const viewDesignationKey = getLoadingKey(RowActionEnum.VIEW, ChangeRequestSectionEnum.DESIGNATION);
const viewFinancingPackageKey = getLoadingKey(RowActionEnum.VIEW, ChangeRequestSectionEnum.FINANCING_PACKAGE);

type AppData = {
  appId: string;
};
type Props = {
  appData?: AppData;
  changeRequestData?: ChangeRequestItem[];
  onReloadData?: () => void;
};

export const ChangeRequestSection: FC<Props> = ({ appData, changeRequestData: data, onReloadData }) => {
  const { appId } = appData || {};
  if (!appId) return null;

  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const lang = useLang();

  const { classes } = useStyles();

  const [popUpVisible, setPopUpVisible] = useState<boolean>(false);
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const historyRef = useRef<ChangeRequestsHistoryrRef>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (appId) {
      historyRef?.current?.reloadData();
    }
  }, [appId]);

  const withLoading = async (key: string, callback: () => Promise<void>) => {
    setLoadingMap((prev) => ({
      ...prev,
      [key]: true,
    }));

    try {
      await callback();
    } finally {
      setLoadingMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const isAllDisabled = Object.keys(loadingMap).length > 0;

  const { openChangeRequestFile } = useDocument();

  const onUpdateStatus = async (action: RowActionEnum, item: ChangeRequestItem) => {
    try {
      await updateChangeRequestStatus(
        appId,
        action === RowActionEnum.APPROVE ? 'approved' : 'rejected',
        item,
        dispatch,
      );

      const content =
        action === RowActionEnum.APPROVE ? 'change request has been approved' : 'change request has been rejected';
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: content,
          },
        ]),
      );
      await onReloadData?.();
      historyRef?.current?.reloadData();
    } catch (err) {
      console.error(err);
    }
  };

  const buildApproveRejectActions = (item: ChangeRequestItem) => {
    const approveKey = getLoadingKey(RowActionEnum.APPROVE, item.section);
    const rejectKey = getLoadingKey(RowActionEnum.REJECT, item.section);
    return [
      {
        type: RowActionEnum.APPROVE,
        onClick: () => withLoading(approveKey, () => onUpdateStatus(RowActionEnum.APPROVE, item)),
        loading: !!loadingMap[approveKey],
        disabled: isAllDisabled,
      },
      {
        type: RowActionEnum.REJECT,
        onClick: () => withLoading(rejectKey, () => onUpdateStatus(RowActionEnum.REJECT, item)),
        loading: !!loadingMap[rejectKey],
        disabled: isAllDisabled,
      },
    ];
  };

  const { mobileItem, emailItem, designationItem, financingPackageItem } = useMemo(() => {
    const mobileItem = data?.find((i) => i.section === ChangeRequestSectionEnum.MOBILE);
    const emailItem = data?.find((i) => i.section === ChangeRequestSectionEnum.EMAIL);
    const designationItem = data?.find((i) => i.section === ChangeRequestSectionEnum.DESIGNATION);
    const financingPackageItem = data?.find((i) => i.section === ChangeRequestSectionEnum.FINANCING_PACKAGE);

    return {
      mobileItem,
      emailItem,
      designationItem,
      financingPackageItem,
    };
  }, [data]);

  return (
    <SectionCard
      title={
        <>
          {t('recruit.personalParticular.cr.changeRequests')}:
          <span className={classes.historyLink} onClick={() => setPopUpVisible(true)}>
            {t('recruit.personalParticular.cr.changeRequestHistory')}
          </span>
        </>
      }
    >
      <ChangeRequestsHistoryDialog
        ref={historyRef}
        appId={appId}
        popUpVisible={popUpVisible}
        setPopUpVisible={setPopUpVisible}
      />
      {data?.length === 0 && t('recruit.personalParticular.cr.noChangeRequestSubmission')}

      <DividerGroup dividerColor="#D1D5DC" paddingY={20}>
        {/* 1. Personal Particular  */}
        {(mobileItem || emailItem) && (
          <Section title={t('recruit.personalParticular.cr.personalParticular')}>
            <DividerGroup>
              {/* New Mobile */}
              {mobileItem && (
                <Row actionAlign="top" actions={buildApproveRejectActions(mobileItem)}>
                  <div className={classes.itemContent}>
                    <div className={classes.fixedWidthCell}>{t('recruit.personalParticular.cr.newMobile')}</div>
                    <div>
                      <span>{mobileItem?.modification?.mobilePhone}</span>
                      {mobileItem?.modification?.isVerified && (
                        <span style={{ marginLeft: 4 }}>{t('recruit.personalParticular.cr.verified')}</span>
                      )}
                    </div>
                  </div>

                  <div className={classes.submittedDate}>
                    {t('recruit.personalParticular.cr.submittedDate')}: {formatDate(mobileItem?.submittedDate)}
                  </div>
                </Row>
              )}
              {/* New Email Address */}
              {emailItem && (
                <Row actionAlign="top" actions={buildApproveRejectActions(emailItem)}>
                  <div className={classes.itemContent}>
                    <div className={classes.fixedWidthCell}>{t('recruit.personalParticular.cr.newEmailAddress')}</div>
                    <div>
                      <a
                        href={`mailto:${emailItem?.modification?.email}`}
                        style={{
                          color: '#ED1B2E',
                          textDecoration: 'underline',
                        }}
                      >
                        {emailItem?.modification?.email}
                      </a>
                      {emailItem?.modification?.isVerified && (
                        <span style={{ marginLeft: 4 }}>{t('recruit.personalParticular.cr.verified')}</span>
                      )}
                    </div>
                  </div>

                  <div className={classes.submittedDate}>
                    {t('recruit.personalParticular.cr.submittedDate')}: {formatDate(emailItem?.submittedDate)}
                  </div>
                </Row>
              )}
            </DividerGroup>
          </Section>
        )}

        {/* 2. Title */}
        {designationItem && (
          <Section title={t('recruit.personalParticular.cr.title')}>
            <DividerGroup>
              {/* Unit Manager (UM) */}
              <Row actionAlign="top" actions={buildApproveRejectActions(designationItem)}>
                <div className={classes.itemContent}>
                  <div className={classes.fixedWidthCell}>
                    {designationItem?.modification?.designation && t(designationItem?.modification?.designation)}
                  </div>
                  <div>
                    {t('recruit.personalParticular.cr.reasonOfRecommendation')}:{' '}
                    {designationItem?.modification?.recommendReason}
                  </div>
                </div>

                <div className={classes.submittedDate}>
                  {t('recruit.personalParticular.cr.submittedDate')}: {formatDate(designationItem?.submittedDate)}
                </div>
              </Row>

              {/* Supplementary Agreement For Managers */}
              <Row
                actions={[
                  {
                    type: RowActionEnum.VIEW,
                    onClick: () =>
                      withLoading(viewDesignationKey, () =>
                        openChangeRequestFile(
                          {
                            lang,
                            appId,
                            pdfType: PdfFileTypeEnum.SupplementaryAgreementForManagers,
                          },
                          t('recruit.personalParticular.cr.supplementaryAgreement'),
                        ),
                      ),
                    loading: !!loadingMap[viewDesignationKey],
                    disabled: isAllDisabled,
                  },
                ]}
              >
                {t('recruit.personalParticular.cr.supplementaryAgreement')}
              </Row>
            </DividerGroup>
          </Section>
        )}

        {/* 3. Update Package */}
        {financingPackageItem && (
          <Section title={t('recruit.personalParticular.cr.updatePackage')}>
            <DividerGroup>
              <Row actionAlign="top" actionMarginTop={4} actions={buildApproveRejectActions(financingPackageItem)}>
                <div className={classes.compactItemContent}>
                  {t('recruit.personalParticular.cr.financingScheme')}:{' '}
                  {financingPackageItem?.modification?.finScheme
                    ? t(financingPackageItem?.modification?.finScheme)
                    : 'N/A'}
                </div>

                <div className={classes.compactItemContent}>
                  {t('recruit.personalParticular.cr.financingCategory')}:{' '}
                  {financingPackageItem?.modification?.finCategory
                    ? t(financingPackageItem?.modification?.finCategory)
                    : 'N/A'}
                </div>

                <div className={classes.compactItemContent}>
                  {t('recruit.personalParticular.cr.monthlyFinancingAmount')}:
                  {financingPackageItem?.modification?.finApplyMonthly
                    ? ' $' + formatAmount(financingPackageItem?.modification?.finApplyMonthly)
                    : 'N/A'}
                </div>

                <div className={classes.submittedDate}>
                  {t('recruit.personalParticular.cr.submittedDate')}: {formatDate(financingPackageItem?.submittedDate)}
                </div>
              </Row>

              {/* Financing Agreement */}
              <Row
                actions={[
                  {
                    type: RowActionEnum.VIEW,
                    onClick: () =>
                      withLoading(viewFinancingPackageKey, () =>
                        openChangeRequestFile(
                          {
                            lang,
                            appId,
                            pdfType: PdfFileTypeEnum.FinancingAgreement,
                          },
                          t('recruit.personalParticular.cr.financingAgreement'),
                        ),
                      ),
                    loading: !!loadingMap[viewFinancingPackageKey],
                    disabled: isAllDisabled,
                  },
                ]}
              >
                {t('recruit.personalParticular.cr.financingAgreement')}
              </Row>
            </DividerGroup>
          </Section>
        )}
      </DividerGroup>
    </SectionCard>
  );
};
